import { useQuery } from '@tanstack/react-query';
import { FC, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { PinIcon, FilterIcon, FilterXIcon } from 'lucide-react';
import ReactDOMServer from 'react-dom/server';
import axios from 'axios';

import { API_BASE_URL } from '@/constants';
import { useFilters } from '@/hooks/FiltersContext';
import { useSidebar } from '@/components/ui/sidebar';
import { NbButton, NbPageHeader } from '@/components/Neobrut';
import { MapSkeleton } from '@/components/Skeletons/MapSkeleton';

import { fromLonLat } from 'ol/proj';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import Cluster from 'ol/source/Cluster';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';
import Overlay from 'ol/Overlay';

export const PetsMap: FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const { filters } = useFilters();

  const {
    data: pets,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['pets-map', filters],
    queryFn: async (): Promise<Array<Pet>> => {
      const requestFilters = { ...filters };
      if (requestFilters.race === 'mestizo') {
        delete requestFilters['race'];
      }

      const response = await axios.get(`${API_BASE_URL}/pets/`, {
        params: {
          is_active: true,
          solved_case: false,
          no_pagination: true,
          ...requestFilters,
        },
      });
      return response.data;
    },
  });

  const navigateToPet = useCallback(
    (petId: number) => {
      navigate(`/pet/${petId}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (!mapRef.current || !pets) return;

    let popoverElement = document.getElementById('popover');
    if (!popoverElement) {
      popoverElement = document.createElement('div');
      popoverElement.id = 'popover';
      popoverElement.className = 'hidden absolute';
      popoverElement.style.background = 'var(--nb-paper)';
      popoverElement.style.border = '2px solid var(--nb-ink)';
      popoverElement.style.boxShadow = '3px 3px 0 var(--nb-ink)';
      popoverElement.style.borderRadius = 'var(--nb-radius)';
      popoverElement.style.padding = '8px';
      popoverElement.style.minWidth = '140px';
      popoverElement.style.maxWidth = '200px';
      document.body.appendChild(popoverElement);
    }

    const popoverOverlay = new Overlay({
      element: popoverElement,
      positioning: 'bottom-center',
      stopEvent: true,
      offset: [0, -10],
    });

    const newMap = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat([-60.7, -32.9468]),
        zoom: 11,
      }),
    });

    const features = pets.map((pet) => {
      return new Feature({
        geometry: new Point(fromLonLat([pet.location.lon, pet.location.lat])),
        petId: pet.id,
      });
    });

    const clusterSource = new Cluster({
      distance: 40,
      source: new VectorSource({
        features,
      }),
    });

    const clusterLayer = new VectorLayer({
      source: clusterSource,
      style: (feature) => {
        const size = feature.get('features').length;
        if (size > 1) {
          return new Style({
            image: new CircleStyle({
              radius: 15,
              fill: new Fill({ color: '#1a1a1a' }),
            }),
            text: new Text({
              text: size.toString(),
              fill: new Fill({ color: '#ffffff' }),
              font: '700 12px "Archivo Black", sans-serif',
            }),
          });
        }
        const petFeature = feature.get('features')[0];
        const pet = pets.find((p) => p.id === petFeature.get('petId'));
        const markerColor = pet?.status === 'lost' ? '#f5f0a8' : '#d9c8f5';
        const pinIconSVG = ReactDOMServer.renderToString(
          <PinIcon fill={markerColor} color="#1a1a1a" size={32} strokeWidth={2.5} />,
        );
        return new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: `data:image/svg+xml;base64,${btoa(pinIconSVG)}`,
          }),
        });
      },
    });

    newMap.addLayer(clusterLayer);
    newMap.addOverlay(popoverOverlay);

    newMap.on('singleclick', (event) => {
      let featureFound = false;

      newMap.forEachFeatureAtPixel(event.pixel, (feature) => {
        const features = feature.get('features');
        if (features && features.length === 1) {
          const petId = features[0].get('petId');
          const pet = pets.find((p) => p.id === petId);
          if (pet) {
            const geometry = feature.getGeometry();
            if (geometry instanceof Point) {
              const coordinates = geometry.getCoordinates();
              popoverOverlay.setPosition(coordinates);

              const safeName = (pet.name ?? '').replace(/[<>"']/g, '');
              const statusLabel = pet.status === 'lost' ? 'PERDIDO' : 'ENCONTRADO';
              const statusFill = pet.status === 'lost' ? 'var(--nb-lost)' : 'var(--nb-found)';
              const heading = pet.status === 'lost' ? safeName || 'Sin nombre' : '¿Lo conocés?';
              const thumb =
                pet.gallery && pet.gallery.length > 0
                  ? `<img src="${pet.gallery[0]}" alt="${safeName}"
                       style="width:100%;height:80px;object-fit:cover;border:2px solid var(--nb-ink);display:block;" />`
                  : '';

              popoverElement!.innerHTML = `
                <div style="display:flex;flex-direction:column;gap:6px;">
                  ${thumb}
                  <div style="font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:700;letter-spacing:0.08em;padding:2px 6px;border:2px solid var(--nb-ink);background:${statusFill};display:inline-block;align-self:flex-start;">
                    ${statusLabel}
                  </div>
                  <div style="font-family:'Archivo Black',sans-serif;font-size:16px;line-height:1;">${heading}</div>
                  <button id="navigate-button"
                    style="font-family:'Archivo',sans-serif;font-weight:700;font-size:12px;padding:6px 10px;background:var(--nb-ink);color:var(--nb-paper);border:2px solid var(--nb-ink);box-shadow:2px 2px 0 var(--nb-ink);border-radius:var(--nb-radius);cursor:pointer;align-self:stretch;">
                    Ver detalle →
                  </button>
                </div>
              `;

              const navigateButton = document.getElementById('navigate-button');
              navigateButton?.addEventListener('click', () => navigateToPet(pet.id));

              popoverElement!.classList.remove('hidden');
              featureFound = true;
            }
          }
        }
      });

      if (!featureFound) {
        popoverOverlay.setPosition(undefined);
        popoverElement!.classList.add('hidden');
      }
    });

    return () => {
      newMap.setTarget(undefined);
      popoverOverlay.setPosition(undefined);
      if (popoverElement) {
        popoverElement.remove();
      }
    };
  }, [pets, navigateToPet]);

  if (isLoading) return <MapSkeleton />;
  if (isError)
    return <div className="flex flex-row justify-center px-4 py-16">Error: {error?.message}</div>;

  const activeFilterCount = filters ? Object.keys(filters).length : 0;

  return (
    <div className="mx-auto w-full max-w-4xl lg:max-w-5xl pb-6">
      <NbPageHeader
        slug="/ mapa"
        title={
          <>
            Todo el<br />
            <span className="nb-highlight">mapa.</span>
          </>
        }
        subtitle="Tocá un pin para ver la publicación."
        fill="urgent"
      />
      <div className="flex items-center justify-between px-4 pt-3">
        <div
          className="text-[11px] font-bold uppercase tracking-[0.08em] opacity-70"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          {pets?.length ?? 0} publicaciones activas
        </div>
        <NbButton fill="ink" size="sm" onClick={toggleSidebar}>
          {activeFilterCount > 0 ? <FilterXIcon size={12} /> : <FilterIcon size={12} />}
          Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </NbButton>
      </div>
      <div className="px-4 pt-3">
        <div
          className="h-[70vh] w-full overflow-hidden"
          style={{
            border: '3px solid var(--nb-ink)',
            boxShadow: '6px 6px 0 var(--nb-ink)',
            borderRadius: 'var(--nb-radius)',
            background: 'var(--nb-paper)',
          }}
        >
          <div ref={mapRef} className="h-full w-full" />
        </div>
      </div>
    </div>
  );
};
